import { FileDownloadOutlined } from '@mui/icons-material';
import { Box, Card, CardContent, CircularProgress, Divider, IconButton, ListItemText, Menu, MenuItem, Table, TableBody, TableCell, TableHead, TableRow, Tooltip, Typography } from '@mui/material';
import { generateClient } from 'aws-amplify/api';
import moment from 'moment';
import { useContext, useEffect, useState } from 'react';
import { UserContext } from '../../contexts/UserContext';
import { esQuery } from '../../graphql/queries';
import { cardStyle } from '../../theme';

const isDev = process.env.REACT_APP_ENV === 'dev';

const MOCK_CALL_DATA = [
	{
		key: 'completed',
		doc_count: 312,
		disposition: {
			buckets: [
				{ key: 'Sale', agent: { buckets: [{ key: 'agent1@test.com', doc_count: 45 }, { key: 'agent2@test.com', doc_count: 38 }] } },
				{ key: 'Not Interested', agent: { buckets: [{ key: 'agent1@test.com', doc_count: 62 }, { key: 'agent3@test.com', doc_count: 41 }] } },
				{ key: 'Voicemail', agent: { buckets: [{ key: 'System', doc_count: 78 }] } },
				{ key: 'No Answer', agent: { buckets: [{ key: 'System', doc_count: 48 }] } },
			]
		}
	},
	{
		key: 'retry',
		doc_count: 688,
		disposition: {
			buckets: [
				{ key: 'Busy', agent: { buckets: [{ key: 'System', doc_count: 195 }] } },
				{ key: 'No Answer - Retry', agent: { buckets: [{ key: 'System', doc_count: 280 }] } },
				{ key: 'Callback Scheduled', agent: { buckets: [{ key: 'agent2@test.com', doc_count: 87 }, { key: 'agent4@test.com', doc_count: 126 }] } },
			]
		}
	}
];

const MOCK_SUPPRESS_COUNT = 154;

const MOCK_PENETRATION_DATA = [
	{ dials: 1, contacts: 420 },
	{ dials: 2, contacts: 285 },
	{ dials: 3, contacts: 148 },
	{ dials: 4, contacts: 67 },
	{ dials: 5, contacts: 23 },
];

const MOCK_AGENT_DISPO_DATA = {
	final: [
		{
			key: 'Sale',
			doc_count: 183,
			by_name: {
				buckets: [
					{ key: 'Sale Complete', doc_count: 145 },
					{ key: 'Partial Sale', doc_count: 38 },
				]
			}
		},
		{
			key: 'Not Interested',
			doc_count: 106,
			by_name: {
				buckets: [
					{ key: 'Not Interested - Price', doc_count: 92 },
					{ key: 'Do Not Call', doc_count: 14 },
				]
			}
		},
	],
	retry: [
		{
			key: 'Callback',
			doc_count: 67,
			by_name: {
				buckets: [
					{ key: 'Callback Requested', doc_count: 67 },
				]
			}
		},
	],
};

const CampaignSummary = ({ campaignId, campaignName, inputStartDate, inputEndDate, callData: callDataProp, suppressCount: suppressCountProp, agentDispoData: agentDispoDataProp }) => {
	const client = generateClient();
	const userContext = useContext(UserContext);
	const [loading, setLoading] = useState(false);
	const [loadingMessage, setLoadingMessage] = useState('');
	const [penetrationData, setPenetrationData] = useState([]);
	const [totalDials, setTotalDials] = useState(0);
	const [downloadAnchorEl, setDownloadAnchorEl] = useState(null);

	const systemAgents = ['0000000', 'System', 'SS Service', 'RingCX'];

	// Use mock data in dev, real data in other envs
	const callData = isDev ? MOCK_CALL_DATA : callDataProp;
	const suppressCount = isDev ? MOCK_SUPPRESS_COUNT : suppressCountProp;
	const agentDispoData = isDev ? MOCK_AGENT_DISPO_DATA : agentDispoDataProp;

	useEffect(() => {
		if (campaignId && inputStartDate && inputEndDate) {
			fetchSummaryData();
		}
	}, [campaignId, inputStartDate, inputEndDate]);

	async function fetchSummaryData() {
		setLoading(true);
		setLoadingMessage('Loading penetration data...');
		try {
			if (isDev) {
				setPenetrationData(MOCK_PENETRATION_DATA);
				setTotalDials(MOCK_PENETRATION_DATA.reduce((sum, d) => sum + (d.dials * d.contacts), 0));
				setLoading(false);
				setLoadingMessage('');
				return;
			}

			// Paginate through composite aggregation to avoid 10k limit
			let allBuckets = [];
			let afterKey = null;
			let pageCount = 0;

			while (true) {
				pageCount++;
				setLoadingMessage(`Loading penetration data... (page ${pageCount})`);

				const response = await getPenetrationPage(afterKey);
				const parsed = JSON.parse(response.data.esQuery);
				const buckets = parsed.aggregations.contact_composite.buckets;

				if (!buckets || buckets.length === 0) break;

				allBuckets = allBuckets.concat(buckets);
				afterKey = parsed.aggregations.contact_composite.after_key;

				// If we got fewer than the page size, we've reached the end
				if (buckets.length < 5000) break;
			}

			// Process aggregated results
			let callCount = 0;
			let attemptAggregations = {};
			for (const bucket of allBuckets) {
				callCount += bucket.doc_count;
				attemptAggregations[bucket.doc_count] = attemptAggregations[bucket.doc_count] ? attemptAggregations[bucket.doc_count] + 1 : 1;
			}

			const chartData = [];
			for (const key in attemptAggregations) {
				chartData.push({ dials: parseInt(key), contacts: attemptAggregations[key] });
			}
			chartData.sort((a, b) => a.dials - b.dials);
			setPenetrationData(chartData);
			setTotalDials(callCount);
		} catch (error) {
			console.error('Error fetching summary data:', error);
		}
		setLoadingMessage('');
		setLoading(false);
	}

	function getPenetrationPage(afterKey) {
		const agg = {
			"contact_composite": {
				"composite": {
					"size": 5000,
					"sources": [
						{ "contact": { "terms": { "field": "activityLogContactId.keyword" } } }
					]
				}
			}
		};

		if (afterKey) {
			agg.contact_composite.composite.after = afterKey;
		}

		return client.graphql({
			query: esQuery,
			variables: {
				model: 'activitylog-*',
				action: '_search',
				query: JSON.stringify({
					"aggs": agg,
					"query": {
						"bool": {
							"must": [
								{ "term": { "type.keyword": "Dial" } },
								{ "term": { "tenant.keyword": userContext.tenantId } },
								{ "term": { "activityLogCampaignId.keyword": campaignId } },
								{
									"range": {
										"timestamp": {
											"gte": inputStartDate,
											"lte": inputEndDate
										}
									}
								}
							],
							"must_not": [
								{ "term": { "disposition.keyword": "Cadence Exhaustion - Retry Increment" } },
								{ "term": { "disposition.keyword": "Agent Specific — Agent Unavailable" } }
							]
						}
					},
					"size": 0
				})
			}
		});
	}

	// Build disposition summary from callData, split by agent vs system AND completed vs retry
	function getDispositionSummary() {
		const agentFinalDispos = [];
		const agentRetryDispos = [];
		const systemFinalDispos = [];
		const systemRetryDispos = [];
		if (!callData) return { agentFinalDispos, agentRetryDispos, systemFinalDispos, systemRetryDispos, agentFinalTotal: 0, agentRetryTotal: 0, systemFinalTotal: 0, systemRetryTotal: 0 };

		for (const status of callData) {
			for (const disposition of status.disposition.buckets) {
				let agentCount = 0;
				let systemCount = 0;
				for (const agent of disposition.agent.buckets) {
					if (systemAgents.includes(agent.key) || !agent.key) {
						systemCount += agent.doc_count;
					} else {
						agentCount += agent.doc_count;
					}
				}
				if (status.key === 'completed') {
					if (agentCount > 0) agentFinalDispos.push({ label: disposition.key, count: agentCount });
					if (systemCount > 0) systemFinalDispos.push({ label: disposition.key, count: systemCount });
				} else {
					if (agentCount > 0) agentRetryDispos.push({ label: disposition.key, count: agentCount });
					if (systemCount > 0) systemRetryDispos.push({ label: disposition.key, count: systemCount });
				}
			}
		}
		agentFinalDispos.sort((a, b) => b.count - a.count);
		agentRetryDispos.sort((a, b) => b.count - a.count);
		systemFinalDispos.sort((a, b) => b.count - a.count);
		systemRetryDispos.sort((a, b) => b.count - a.count);

		const agentFinalTotal = agentFinalDispos.reduce((sum, d) => sum + d.count, 0);
		const agentRetryTotal = agentRetryDispos.reduce((sum, d) => sum + d.count, 0);
		const systemFinalTotal = systemFinalDispos.reduce((sum, d) => sum + d.count, 0);
		const systemRetryTotal = systemRetryDispos.reduce((sum, d) => sum + d.count, 0);

		return { agentFinalDispos, agentRetryDispos, systemFinalDispos, systemRetryDispos, agentFinalTotal, agentRetryTotal, systemFinalTotal, systemRetryTotal };
	}

	function getTotalDialCount() {
		if (!callData) return 0;
		let total = 0;
		for (const status of callData) {
			for (const disposition of status.disposition.buckets) {
				for (const agent of disposition.agent.buckets) {
					total += agent.doc_count;
				}
			}
		}
		return total;
	}

	function downloadSummaryCSV() {
		const { agentFinalDispos, agentRetryDispos, systemFinalDispos, systemRetryDispos, agentFinalTotal, agentRetryTotal, systemFinalTotal, systemRetryTotal } = getDispositionSummary();
		const dialTotal = getTotalDialCount();

		let csv = `Campaign Summary Report\n`;
		csv += `Campaign,${campaignName}\n`;
		csv += `Date Range,${moment(inputStartDate).format('MMM D YYYY')} - ${moment(inputEndDate).format('MMM D YYYY')}\n\n`;

		// Dials
		csv += `DIALS\n`;
		csv += `Total Dials,${dialTotal}\n`;
		csv += `Unique Contacts Dialed,${penetrationData.reduce((sum, d) => sum + d.contacts, 0)}\n`;
		csv += `Suppressions,${suppressCount}\n\n`;

		// Agent Disposition Final
		if (agentDispoData?.final?.length > 0) {
			csv += `AGENT DISPOSITION FINAL\n`;
			csv += `Group,Disposition,Count\n`;
			for (const group of agentDispoData.final) {
				if (group.by_name.buckets.length > 0) {
					for (const dispo of group.by_name.buckets) {
						csv += `"${group.key}","${dispo.key}",${dispo.doc_count}\n`;
					}
				} else {
					csv += `"${group.key}","${group.key}",${group.doc_count}\n`;
				}
			}
			csv += `,,${agentFinalTotal}\n\n`;
		} else {
			csv += `AGENT DISPOSITION FINAL\n`;
			csv += `Disposition,Count\n`;
			for (const dispo of agentFinalDispos) {
				csv += `"${dispo.label}",${dispo.count}\n`;
			}
			csv += `Total,${agentFinalTotal}\n\n`;
		}

		// Agent Disposition Retry
		if (agentDispoData?.retry?.length > 0) {
			csv += `AGENT DISPOSITION RETRY\n`;
			csv += `Group,Disposition,Count\n`;
			for (const group of agentDispoData.retry) {
				if (group.by_name.buckets.length > 0) {
					for (const dispo of group.by_name.buckets) {
						csv += `"${group.key}","${dispo.key}",${dispo.doc_count}\n`;
					}
				} else {
					csv += `"${group.key}","${group.key}",${group.doc_count}\n`;
				}
			}
			csv += `,,${agentRetryTotal}\n\n`;
		} else {
			csv += `AGENT DISPOSITION RETRY\n`;
			csv += `Disposition,Count\n`;
			for (const dispo of agentRetryDispos) {
				csv += `"${dispo.label}",${dispo.count}\n`;
			}
			csv += `Total,${agentRetryTotal}\n\n`;
		}

		// System Disposition Final
		csv += `SYSTEM DISPOSITION FINAL\n`;
		csv += `Disposition,Count\n`;
		for (const dispo of systemFinalDispos) {
			csv += `"${dispo.label}",${dispo.count}\n`;
		}
		csv += `Total,${systemFinalTotal}\n\n`;

		// System Disposition Retry
		csv += `SYSTEM DISPOSITION RETRY\n`;
		csv += `Disposition,Count\n`;
		for (const dispo of systemRetryDispos) {
			csv += `"${dispo.label}",${dispo.count}\n`;
		}
		csv += `Total,${systemRetryTotal}\n\n`;

		// Penetration
		csv += `PENETRATION\n`;
		csv += `Total Dials,${totalDials}\n`;
		csv += `Unique Contacts,${penetrationData.reduce((sum, d) => sum + d.contacts, 0)}\n`;
		csv += `Dials,Contacts\n`;
		for (const pen of penetrationData) {
			csv += `${pen.dials},${pen.contacts}\n`;
		}

		const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
		const url = URL.createObjectURL(blob);
		const link = document.createElement('a');
		link.href = url;
		link.setAttribute('download', `${campaignName}_Summary_${moment(inputStartDate).format('YYYY-MM-DD')}_${moment(inputEndDate).format('YYYY-MM-DD')}.csv`);
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);
		URL.revokeObjectURL(url);
	}

	async function downloadSummaryPDF() {
		const { jsPDF } = await import('jspdf');
		await import('jspdf-autotable');

		const { agentFinalDispos, agentRetryDispos, systemFinalDispos, systemRetryDispos, agentFinalTotal, agentRetryTotal, systemFinalTotal, systemRetryTotal } = getDispositionSummary();
		const dialTotal = getTotalDialCount();
		const uniqueContacts = penetrationData.reduce((sum, d) => sum + d.contacts, 0);

		const doc = new jsPDF();
		const pageWidth = doc.internal.pageSize.getWidth();
		let y = 20;

		// Title
		doc.setFontSize(16);
		doc.text('Campaign Summary Report', pageWidth / 2, y, { align: 'center' });
		y += 10;
		doc.setFontSize(11);
		doc.text(`Campaign: ${campaignName}`, 14, y);
		y += 6;
		doc.text(`Date Range: ${moment(inputStartDate).format('MMM D, YYYY')} - ${moment(inputEndDate).format('MMM D, YYYY')}`, 14, y);
		y += 12;

		// Dials Section
		doc.setFontSize(13);
		doc.text('Dials', 14, y);
		y += 8;
		doc.setFontSize(10);
		doc.text(`Total Dials: ${dialTotal}`, 14, y);
		y += 5;
		doc.text(`Unique Contacts Dialed: ${uniqueContacts}`, 14, y);
		y += 5;
		doc.text(`Suppressions: ${suppressCount}`, 14, y);
		y += 10;

		// Helper to add a disposition table
		const addDispoTable = (title, data, total) => {
			doc.setFontSize(12);
			doc.text(title, 14, y);
			y += 2;
			const tableData = data.map(d => [d.label, d.count.toString()]);
			tableData.push([{ content: 'Total', styles: { fontStyle: 'bold' } }, { content: total.toString(), styles: { fontStyle: 'bold' } }]);
			doc.autoTable({
				startY: y,
				head: [['Disposition', 'Count']],
				body: tableData,
				theme: 'grid',
				headStyles: { fillColor: [66, 133, 244] },
				margin: { left: 14, right: 14 },
				styles: { fontSize: 9 },
			});
			y = doc.lastAutoTable.finalY + 10;
		};

		// Helper to add a grouped disposition table
		const addGroupedDispoTable = (title, groupData, total) => {
			if (y > 250) { doc.addPage(); y = 20; }
			doc.setFontSize(12);
			doc.text(title, 14, y);
			y += 2;
			const tableData = [];
			for (const group of groupData) {
				if (group.by_name.buckets.length > 0) {
					for (const dispo of group.by_name.buckets) {
						tableData.push([group.key, dispo.key, dispo.doc_count.toString()]);
					}
				} else {
					tableData.push([group.key, group.key, group.doc_count.toString()]);
				}
			}
			tableData.push([{ content: '', styles: {} }, { content: 'Total', styles: { fontStyle: 'bold' } }, { content: total.toString(), styles: { fontStyle: 'bold' } }]);
			doc.autoTable({
				startY: y,
				head: [['Group', 'Disposition', 'Count']],
				body: tableData,
				theme: 'grid',
				headStyles: { fillColor: [66, 133, 244] },
				margin: { left: 14, right: 14 },
				styles: { fontSize: 9 },
			});
			y = doc.lastAutoTable.finalY + 10;
		};

		if (agentDispoData?.final?.length > 0) {
			addGroupedDispoTable('Agent Disposition Final', agentDispoData.final, agentFinalTotal);
		} else {
			addDispoTable('Agent Disposition Final', agentFinalDispos, agentFinalTotal);
		}
		if (agentDispoData?.retry?.length > 0) {
			addGroupedDispoTable('Agent Disposition Retry', agentDispoData.retry, agentRetryTotal);
		} else {
			addDispoTable('Agent Disposition Retry', agentRetryDispos, agentRetryTotal);
		}
		addDispoTable('System Disposition Final', systemFinalDispos, systemFinalTotal);
		addDispoTable('System Disposition Retry', systemRetryDispos, systemRetryTotal);

		// Penetration Section
		if (y > 250) { doc.addPage(); y = 20; }
		doc.setFontSize(13);
		doc.text('Penetration', 14, y);
		y += 8;
		doc.setFontSize(10);
		doc.text(`Total Dials: ${totalDials}`, 14, y);
		y += 5;
		doc.text(`Unique Contacts: ${uniqueContacts}`, 14, y);
		y += 5;
		if (uniqueContacts > 0) {
			doc.text(`Avg Dials Per Contact: ${(totalDials / uniqueContacts).toFixed(2)}`, 14, y);
			y += 5;
		}
		y += 3;

		const penTableData = penetrationData.map(p => [`${p.dials} ${p.dials === 1 ? 'Dial' : 'Dials'}`, p.contacts.toString()]);
		doc.autoTable({
			startY: y,
			head: [['Dials', 'Contacts']],
			body: penTableData,
			theme: 'grid',
			headStyles: { fillColor: [66, 133, 244] },
			margin: { left: 14, right: 14 },
			styles: { fontSize: 9 },
		});

		doc.save(`${campaignName}_Summary_${moment(inputStartDate).format('YYYY-MM-DD')}_${moment(inputEndDate).format('YYYY-MM-DD')}.pdf`);
	}

	const { agentFinalDispos, agentRetryDispos, systemFinalDispos, systemRetryDispos, agentFinalTotal, agentRetryTotal, systemFinalTotal, systemRetryTotal } = getDispositionSummary();
	const dialTotal = getTotalDialCount();

	return (
		<Box display="flex" flexDirection="column" gap={2} width="100%">
			{loading && (
				<Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" height="200px" gap={2}>
					<CircularProgress color="primary" />
					{loadingMessage && <Typography variant="body2" color="textSecondary">{loadingMessage}</Typography>}
				</Box>
			)}
			{!loading && (
				<>
					<Box display="flex" justifyContent="flex-end">
						<Tooltip title="Download Summary">
							<IconButton onClick={(e) => setDownloadAnchorEl(e.currentTarget)}>
								<FileDownloadOutlined color="primary" />
							</IconButton>
						</Tooltip>
						<Menu
							anchorEl={downloadAnchorEl}
							open={Boolean(downloadAnchorEl)}
							onClose={() => setDownloadAnchorEl(null)}
						>
							<MenuItem onClick={() => { downloadSummaryCSV(); setDownloadAnchorEl(null); }}>
								<ListItemText>Download CSV</ListItemText>
							</MenuItem>
							<MenuItem onClick={() => { downloadSummaryPDF(); setDownloadAnchorEl(null); }}>
								<ListItemText>Download PDF</ListItemText>
							</MenuItem>
						</Menu>
					</Box>

					{/* Dials Section */}
					<Card variant="outlined" sx={cardStyle}>
						<CardContent>
							<Typography variant="h5" gutterBottom>Dials</Typography>
							<Box display="flex" gap={4} alignItems="center">
								<Box display="flex" flexDirection="column" alignItems="center">
									<Typography variant="h3">{dialTotal}</Typography>
									<Typography variant="body2">Total Dials</Typography>
								</Box>
								<Divider orientation="vertical" flexItem />
								<Box display="flex" flexDirection="column" alignItems="center">
									<Typography variant="h3">{penetrationData.reduce((sum, d) => sum + d.contacts, 0)}</Typography>
									<Typography variant="body2">Unique Contacts Dialed</Typography>
								</Box>
								<Divider orientation="vertical" flexItem />
								<Box display="flex" flexDirection="column" alignItems="center">
									<Typography variant="h3">{suppressCount}</Typography>
									<Typography variant="body2">Suppressions</Typography>
								</Box>
							</Box>
						</CardContent>
					</Card>

					{/* Disposition Breakdown */}
					<Card variant="outlined" sx={cardStyle}>
						<CardContent>
							<Typography variant="h5" gutterBottom>Disposition Breakdown</Typography>
							<Box display="grid" gridTemplateColumns="1fr 1fr" gap={2}>
								{/* Agent Disposition Final */}
								<Box>
									<Typography variant="h6" gutterBottom>Agent Disposition Final</Typography>
									<Table size="small">
										<TableHead>
											<TableRow>
												<TableCell sx={{ width: '180px', whiteSpace: 'nowrap' }}>Group</TableCell>
												<TableCell>Disposition</TableCell>
												<TableCell align="right">Count</TableCell>
											</TableRow>
										</TableHead>
										<TableBody>
											{agentDispoData?.final?.length > 0 ? (
												agentDispoData.final.map((group) => (
													group.by_name.buckets.length > 0 ? (
														group.by_name.buckets.map((dispo, idx) => (
															<TableRow key={`final-${group.key}-${dispo.key}`}>
																{idx === 0 && (
																	<TableCell
																		rowSpan={group.by_name.buckets.length}
																		sx={{
																			fontWeight: 'bold',
																			verticalAlign: 'middle',
																			borderRight: '3px solid',
																			borderRightColor: 'primary.main',
																			textAlign: 'center',
																			whiteSpace: 'nowrap',
																		}}
																	>
																		{group.key}
																	</TableCell>
																)}
																<TableCell>{dispo.key}</TableCell>
																<TableCell align="right">{dispo.doc_count}</TableCell>
															</TableRow>
														))
													) : (
														<TableRow key={`final-${group.key}`}>
															<TableCell sx={{ fontWeight: 'bold', borderRight: '3px solid', borderRightColor: 'primary.main', textAlign: 'center', whiteSpace: 'nowrap' }}>{group.key}</TableCell>
															<TableCell>{group.key}</TableCell>
															<TableCell align="right">{group.doc_count}</TableCell>
														</TableRow>
													)
												))
											) : (
												agentFinalDispos.map((dispo) => (
													<TableRow key={dispo.label}>
														<TableCell />
														<TableCell>{dispo.label}</TableCell>
														<TableCell align="right">{dispo.count}</TableCell>
													</TableRow>
												))
											)}
											<TableRow>
												<TableCell />
												<TableCell><strong>Total</strong></TableCell>
												<TableCell align="right"><strong>{agentFinalTotal}</strong></TableCell>
											</TableRow>
										</TableBody>
									</Table>
								</Box>
								{/* Agent Disposition Retry */}
								<Box>
									<Typography variant="h6" gutterBottom>Agent Disposition Retry</Typography>
									<Table size="small">
										<TableHead>
											<TableRow>
												<TableCell sx={{ width: '180px', whiteSpace: 'nowrap' }}>Group</TableCell>
												<TableCell>Disposition</TableCell>
												<TableCell align="right">Count</TableCell>
											</TableRow>
										</TableHead>
										<TableBody>
											{agentDispoData?.retry?.length > 0 ? (
												agentDispoData.retry.map((group) => (
													group.by_name.buckets.length > 0 ? (
														group.by_name.buckets.map((dispo, idx) => (
															<TableRow key={`retry-${group.key}-${dispo.key}`}>
																{idx === 0 && (
																	<TableCell
																		rowSpan={group.by_name.buckets.length}
																		sx={{
																			fontWeight: 'bold',
																			verticalAlign: 'middle',
																			borderRight: '3px solid',
																			borderRightColor: 'primary.main',
																			textAlign: 'center',
																			whiteSpace: 'nowrap',
																		}}
																	>
																		{group.key}
																	</TableCell>
																)}
																<TableCell>{dispo.key}</TableCell>
																<TableCell align="right">{dispo.doc_count}</TableCell>
															</TableRow>
														))
													) : (
														<TableRow key={`retry-${group.key}`}>
															<TableCell sx={{ fontWeight: 'bold', borderRight: '3px solid', borderRightColor: 'primary.main', textAlign: 'center', whiteSpace: 'nowrap' }}>{group.key}</TableCell>
															<TableCell>{group.key}</TableCell>
															<TableCell align="right">{group.doc_count}</TableCell>
														</TableRow>
													)
												))
											) : (
												agentRetryDispos.map((dispo) => (
													<TableRow key={dispo.label}>
														<TableCell />
														<TableCell>{dispo.label}</TableCell>
														<TableCell align="right">{dispo.count}</TableCell>
													</TableRow>
												))
											)}
											<TableRow>
												<TableCell />
												<TableCell><strong>Total</strong></TableCell>
												<TableCell align="right"><strong>{agentRetryTotal}</strong></TableCell>
											</TableRow>
										</TableBody>
									</Table>
								</Box>
								{/* System Disposition Final */}
								<Box>
									<Typography variant="h6" gutterBottom>System Disposition Final</Typography>
									<Table size="small">
										<TableHead>
											<TableRow>
												<TableCell>Disposition</TableCell>
												<TableCell align="right">Count</TableCell>
											</TableRow>
										</TableHead>
										<TableBody>
											{systemFinalDispos.map((dispo) => (
												<TableRow key={dispo.label}>
													<TableCell>{dispo.label}</TableCell>
													<TableCell align="right">{dispo.count}</TableCell>
												</TableRow>
											))}
											<TableRow>
												<TableCell><strong>Total</strong></TableCell>
												<TableCell align="right"><strong>{systemFinalTotal}</strong></TableCell>
											</TableRow>
										</TableBody>
									</Table>
								</Box>
								{/* System Disposition Retry */}
								<Box>
									<Typography variant="h6" gutterBottom>System Disposition Retry</Typography>
									<Table size="small">
										<TableHead>
											<TableRow>
												<TableCell>Disposition</TableCell>
												<TableCell align="right">Count</TableCell>
											</TableRow>
										</TableHead>
										<TableBody>
											{systemRetryDispos.map((dispo) => (
												<TableRow key={dispo.label}>
													<TableCell>{dispo.label}</TableCell>
													<TableCell align="right">{dispo.count}</TableCell>
												</TableRow>
											))}
											<TableRow>
												<TableCell><strong>Total</strong></TableCell>
												<TableCell align="right"><strong>{systemRetryTotal}</strong></TableCell>
											</TableRow>
										</TableBody>
									</Table>
								</Box>
							</Box>
						</CardContent>
					</Card>

					{/* Penetration */}
					<Card variant="outlined" sx={cardStyle}>
						<CardContent>
							<Typography variant="h5" gutterBottom>Penetration</Typography>
							<Box display="flex" gap={4} alignItems="center" mb={2}>
								<Box display="flex" flexDirection="column" alignItems="center">
									<Typography variant="h4">{totalDials}</Typography>
									<Typography variant="body2">Total Dials</Typography>
								</Box>
								<Divider orientation="vertical" flexItem />
								<Box display="flex" flexDirection="column" alignItems="center">
									<Typography variant="h4">{penetrationData.reduce((sum, d) => sum + d.contacts, 0)}</Typography>
									<Typography variant="body2">Unique Contacts</Typography>
								</Box>
								{penetrationData.reduce((sum, d) => sum + d.contacts, 0) > 0 && (
									<>
										<Divider orientation="vertical" flexItem />
										<Box display="flex" flexDirection="column" alignItems="center">
											<Typography variant="h4">{(totalDials / penetrationData.reduce((sum, d) => sum + d.contacts, 0)).toFixed(2)}</Typography>
											<Typography variant="body2">Avg Dials Per Contact</Typography>
										</Box>
									</>
								)}
							</Box>
							<Table size="small">
								<TableHead>
									<TableRow>
										<TableCell>Dials</TableCell>
										<TableCell align="right">Contacts</TableCell>
									</TableRow>
								</TableHead>
								<TableBody>
									{penetrationData.map((pen) => (
										<TableRow key={pen.dials}>
											<TableCell>{pen.dials} {pen.dials === 1 ? 'Dial' : 'Dials'}</TableCell>
											<TableCell align="right">{pen.contacts}</TableCell>
										</TableRow>
									))}
								</TableBody>
							</Table>
						</CardContent>
					</Card>

				</>
			)}
		</Box>
	);
};

export default CampaignSummary;
